import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Play, X, ChevronRight, ChevronLeft, Sparkles, FileUp, Settings2, Download, CheckCircle2, Rocket, Zap } from 'lucide-react';

const ONBOARDING_STEPS = [
  {
    title: 'Upload Your File',
    description: 'Just drag & drop your CSV or Excel file. We handle files with 100k+ rows — no sweat.',
    icon: FileUp,
    color: 'bg-indigo-500',
  },
  {
    title: 'Pick What to Clean',
    description: 'Click on any column and we\'ll auto-suggest the best cleaning rules for your data type.',
    icon: Settings2,
    color: 'bg-emerald-500',
  },
  {
    title: 'Preview Changes Instantly',
    description: 'See before & after for every cell. Fix phone formats, standardize dropdowns, validate emails — all in real-time.',
    icon: Sparkles,
    color: 'bg-amber-500',
  },
  {
    title: 'Export & You\'re Done!',
    description: 'Download your squeaky-clean file as CSV or Excel. Ready to import straight into your CRM.',
    icon: Download,
    color: 'bg-purple-500',
  },
];

const OnboardingVideoModal = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showVideo, setShowVideo] = useState(false);

  // localStorage check is done in the hook, not here

  const handleComplete = () => {
    localStorage.setItem('crm_cleaner_onboarding_seen', 'true');
    onComplete?.();
    onClose();
  };

  const handleSkip = () => {
    handleComplete();
  };

  const nextStep = () => {
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const currentStepData = ONBOARDING_STEPS[currentStep];
  const Icon = currentStepData.icon;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl" data-testid="onboarding-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Zap className="w-5 h-5 text-indigo-500" />
            You&apos;re all set! Here&apos;s the quick tour
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Perfect — now let&apos;s show you how easy it is to clean your data in 4 quick steps.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          {/* Step Progress */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {ONBOARDING_STEPS.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentStep(idx)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  idx === currentStep 
                    ? 'bg-indigo-500 w-6' 
                    : idx < currentStep 
                      ? 'bg-indigo-300' 
                      : 'bg-slate-200'
                }`}
                data-testid={`onboarding-step-${idx}`}
              />
            ))}
          </div>

          {/* Step Content */}
          <div className="text-center py-6">
            <div className={`w-20 h-20 ${currentStepData.color} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg`}>
              <Icon className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Step {currentStep + 1}: {currentStepData.title}
            </h3>
            <p className="text-slate-600 max-w-sm mx-auto">
              {currentStepData.description}
            </p>
          </div>

          {/* Video Embed Placeholder */}
          {showVideo && (
            <div className="aspect-video bg-slate-900 rounded-lg mb-4 flex items-center justify-center">
              <div className="text-center text-slate-400">
                <Play className="w-12 h-12 mx-auto mb-2" />
                <p className="text-sm">Video coming soon</p>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="text-slate-500 hover:text-slate-700"
              data-testid="onboarding-skip-btn"
            >
              Skip tour
            </Button>

            <div className="flex items-center gap-2">
              {currentStep > 0 && (
                <Button
                  variant="outline"
                  onClick={prevStep}
                  data-testid="onboarding-prev-btn"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
              )}
              
              <Button
                onClick={nextStep}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="onboarding-next-btn"
              >
                {currentStep < ONBOARDING_STEPS.length - 1 ? (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Get Started
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Hook to manage onboarding state
// Note: Auto-trigger is disabled - the tour is manually triggered by DashboardPage 
// AFTER the persona question (ExperienceSurveyModal) is completed
export const useOnboarding = () => {
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Removed auto-trigger logic - now controlled by DashboardPage flow:
  // 1. Persona Question (ExperienceSurveyModal)
  // 2. Then Product Tour (this modal)
  // 3. Then Glowing Upload Box

  const openOnboarding = () => setShowOnboarding(true);
  const closeOnboarding = () => setShowOnboarding(false);

  return { showOnboarding, openOnboarding, closeOnboarding };
};

export default OnboardingVideoModal;
